import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dropdown, List, Typography, Space, notification, Spin } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { notificationsService } from "../../api/notifications";
import {
  requestNotificationPermission,
  onMessageListener,
} from "../../config/firebase";
import Badge from "../Report/Badge/Badge";
import "./NotificationsDropdown.css";

const { Text } = Typography;

const NotificationsDropdown = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [processedNotifications, setProcessedNotifications] = useState(
    new Set()
  );
  // Используем ref для синхронного доступа к счетчику ошибок
  const lastErrorTimeRef = useRef(0);
  const consecutiveErrorsRef = useRef(0);
  const navigate = useNavigate();

  // Функция для сортировки уведомлений по дате (новые сверху)
  const sortNotifications = (notifications) => {
    return notifications.sort((a, b) => {
      // Предполагаем, что у уведомлений есть поле created_at, timestamp или date
      const dateA = new Date(a.created_at || a.timestamp || a.date || 0);
      const dateB = new Date(b.created_at || b.timestamp || b.date || 0);
      return dateB - dateA; // Сортировка по убыванию (новые сверху)
    });
  };

  const fetchNotifications = useCallback(
    async (force = false) => {
      // Защита от дублирующих запросов
      if (isLoading && !force) {
        return;
      }

      const now = Date.now();

      // Если были ошибки сети, используем экспоненциальную задержку
      // Даже при force=true проверяем ошибки, но с меньшей задержкой
      // Используем ref для синхронной проверки
      if (consecutiveErrorsRef.current > 0) {
        const backoffDelay = force
          ? Math.min(
              30000 * Math.pow(2, consecutiveErrorsRef.current - 1),
              120000
            ) // Для force: максимум 2 минуты
          : Math.min(
              60000 * Math.pow(2, consecutiveErrorsRef.current - 1),
              300000
            ); // Для обычных: максимум 5 минут
        if (now - lastErrorTimeRef.current < backoffDelay) {
          console.log(
            `Backoff активен: пропускаем запрос. Ошибок: ${consecutiveErrorsRef.current}, задержка: ${backoffDelay}ms`
          );
          return; // Не делаем запрос, если еще не прошло время backoff
        }
      }

      // Кэширование: не делаем запрос чаще чем раз в 60 секунд (только если нет ошибок)
      if (
        !force &&
        consecutiveErrorsRef.current === 0 &&
        now - lastFetchTime < 60000
      ) {
        return;
      }

      try {
        setIsLoading(true);
        const [unreadNotifications, count] = await Promise.all([
          notificationsService.getUnreadNotifications(),
          notificationsService.getUnreadCount(),
        ]);

        // Показываем только непрочитанные уведомления, отсортированные по дате
        const sortedNotifications = sortNotifications(
          unreadNotifications || []
        );
        setNotifications(sortedNotifications);
        setUnreadCount(count);
        setLastFetchTime(now);
        // Сбрасываем счетчик ошибок при успехе
        consecutiveErrorsRef.current = 0;
        lastErrorTimeRef.current = 0;
      } catch (error) {
        console.error("Error fetching notifications:", error);

        // Проверяем статус ошибки из объекта или из сообщения
        const errorStatus =
          error.status ||
          (error.message.match(/status: (\d+)/)?.[1]
            ? parseInt(error.message.match(/status: (\d+)/)[1])
            : null);

        // Проверяем, является ли это ошибкой сети/сервера
        // Считаем ошибкой сети: сетевые ошибки, 5xx ошибки сервера, таймауты
        const isNetworkError =
          error.message.includes("Failed to fetch") ||
          error.message.includes("NetworkError") ||
          error.message.includes("ERR_CONNECTION") ||
          error.message.includes("ERR_INTERNET_DISCONNECTED") ||
          error.message.includes("timeout") ||
          (errorStatus >= 500 && errorStatus < 600) || // 5xx ошибки сервера
          errorStatus === 0; // Ошибка сети (статус 0 обычно означает CORS или сетевую ошибку)

        if (isNetworkError) {
          // Увеличиваем счетчик ошибок и запоминаем время последней ошибки
          consecutiveErrorsRef.current += 1;
          lastErrorTimeRef.current = now;

          console.log(
            `Ошибка сети обнаружена. Счетчик ошибок: ${consecutiveErrorsRef.current}, время: ${now}`
          );

          // Не показываем ошибку пользователю при каждой попытке, только при первой
          if (consecutiveErrorsRef.current === 1) {
            notification.error({
              message: "Ошибка подключения",
              description:
                "Сервер недоступен. Повторные попытки будут выполняться реже.",
              placement: "topRight",
              duration: 5,
            });
          }
        } else {
          // Для других ошибок показываем уведомление только если это не 4xx ошибки (клиентские)
          if (!errorStatus || errorStatus < 400 || errorStatus >= 500) {
            notification.error({
              message: "Ошибка загрузки",
              description: "Не удалось загрузить уведомления",
              placement: "topRight",
            });
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, lastFetchTime]
  );

  const handleNotificationClick = async (notificationItem) => {
    if (isMarkingRead) return; // Предотвращаем множественные клики

    try {
      setIsMarkingRead(true);

      // Отмечаем уведомление как прочитанное
      await notificationsService.markNotificationAsRead(notificationItem.id);

      // Обновляем счетчик непрочитанных уведомлений
      const newCount = await notificationsService.getUnreadCount();
      setUnreadCount(newCount);

      // Обновляем список уведомлений (принудительно)
      await fetchNotifications(true);

      // Получаем тип уведомления из data.request_type
      const requestType = notificationItem.data?.request_type;

      // Редиректим на соответствующую страницу в зависимости от типа уведомления
      switch (requestType) {
        case "day_off":
          navigate("/approvals/dayoff-approval");
          // Показываем информативное сообщение
          notification.info({
            message: "Переход на страницу согласования отгулов",
            description: "Если заявка уже обработана, список может быть пустым",
            placement: "topRight",
            duration: 3,
          });
          break;
        case "overtime":
          navigate("/approvals/overtime-approval");
          // Показываем информативное сообщение
          notification.info({
            message: "Переход на страницу согласования переработок",
            description: "Если заявка уже обработана, список может быть пустым",
            placement: "topRight",
            duration: 3,
          });
          break;
        case "report_reminder":
          navigate("/report/new");
          break;
        default:
          console.warn("Неизвестный тип уведомления:", requestType);
          notification.info({
            message: "Уведомление",
            description:
              "Переход на страницу не поддерживается для данного типа уведомления",
            placement: "topRight",
          });
          break;
      }
    } catch (error) {
      console.error("Error handling notification click:", error);
      notification.error({
        message: "Ошибка",
        description: "Не удалось обработать уведомление",
        placement: "topRight",
      });
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (isMarkingAllRead || notifications.length === 0) return;

    try {
      setIsMarkingAllRead(true);

      // Отмечаем каждое уведомление как прочитанное (все уведомления в списке уже непрочитанные)
      const markPromises = notifications.map((notification) =>
        notificationsService.markNotificationAsRead(notification.id)
      );

      await Promise.all(markPromises);

      // Обновляем счетчик и список уведомлений (принудительно)
      await fetchNotifications(true);

      notification.success({
        message: "Успешно",
        description: `Отмечено как прочитанные: ${notifications.length} уведомлений`,
        placement: "topRight",
        duration: 2,
      });
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      notification.error({
        message: "Ошибка",
        description: "Не удалось отметить уведомления как прочитанные",
        placement: "topRight",
      });
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  useEffect(() => {
    // Запрашиваем разрешение на уведомления только если токен еще не зарегистрирован
    const existingToken = localStorage.getItem("firebaseToken");
    if (!existingToken) {
      requestNotificationPermission()
        .then((token) => {
          if (token) {
            // Отправляем токен на бэкенд для сохранения
            notificationsService.registerToken(token).catch((error) => {
              console.error("Error registering Firebase token:", error);
            });
          }
        })
        .catch((error) => {
          console.error("Notification permission error:", error);
        });
    }

    // Подписываемся на входящие сообщения (только для foreground)
    onMessageListener()
      .then((payload) => {
        // Создаем уникальный ключ для дедупликации (используем тот же формат что и в Service Worker)
        const notificationKey = `${payload.notification.title}_${
          payload.notification.body
        }_${payload.data?.id || ""}`;

        // Проверяем, не обрабатывали ли мы уже это уведомление
        if (!processedNotifications.has(notificationKey)) {
          setProcessedNotifications(
            (prev) => new Set([...prev, notificationKey])
          );

          // Показываем уведомление только если приложение активно
          if (document.hasFocus()) {
            notification.info({
              message: payload.notification.title,
              description: payload.notification.body,
              placement: "topRight",
            });
          }
        }

        // Обновляем список уведомлений (принудительно)
        fetchNotifications(true);
      })
      .catch((error) => {
        console.error("Error receiving message:", error);
      });

    // Слушаем события от service worker для background уведомлений
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "NOTIFICATION_RECEIVED") {
          // Проверяем дедупликацию по ключу от Service Worker
          const notificationKey = event.data.notificationKey;
          if (notificationKey && !processedNotifications.has(notificationKey)) {
            setProcessedNotifications(
              (prev) => new Set([...prev, notificationKey])
            );
          }

          // Обновляем список уведомлений (принудительно)
          fetchNotifications(true);
        }
      });
    }

    // Загружаем начальные уведомления
    fetchNotifications();

    // Очищаем кэш дедупликации каждые 5 минут
    const cleanupInterval = setInterval(() => {
      setProcessedNotifications(new Set());
    }, 5 * 60 * 1000);

    // Переменные для отслеживания активности пользователя
    // let lastActivity = Date.now();
    let isUserActive = true;

    // Функция для обновления времени последней активности
    // Добавляем debounce для предотвращения множественных вызовов
    let activityTimeout = null;
    const updateActivity = () => {
      // Очищаем предыдущий таймаут
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }

      // Устанавливаем новый таймаут с задержкой 2 секунды
      activityTimeout = setTimeout(() => {
        if (!isUserActive) {
          isUserActive = true;
          // Если пользователь стал активным, обновляем уведомления (принудительно)
          // fetchNotifications сам проверит ошибки сети и применит backoff
          fetchNotifications(true);
        }
      }, 2000);
    };

    // Слушаем события активности пользователя
    // Убрали mousemove - слишком частое событие, вызывает много запросов
    const activityEvents = [
      "mousedown",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    // УБИРАЕМ ИНТЕРВАЛ - уведомления будут обновляться только при:
    // 1. Получении push-уведомлений
    // 2. Возвращении активности пользователя
    // 3. Принудительном обновлении (клики, отметка как прочитанное)

    // Очистка при размонтировании
    return () => {
      clearInterval(cleanupInterval);
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [fetchNotifications, processedNotifications]);

  const getNotificationIcon = (notificationItem) => {
    const requestType = notificationItem.data?.request_type;
    switch (requestType) {
      case "day_off":
        return "🏖️";
      case "overtime":
        return "⏰";
      case "report_reminder":
        return "📋";
      default:
        return "🔔";
    }
  };

  const getNotificationTypeLabel = (notificationItem) => {
    const requestType = notificationItem.data?.request_type;
    switch (requestType) {
      case "day_off":
        return "Отгул";
      case "overtime":
        return "Переработка";
      case "report_reminder":
        return "Напоминание";
      default:
        return "Уведомление";
    }
  };

  const getNotificationTitle = (notificationItem) => {
    const requestType = notificationItem.data?.request_type;
    switch (requestType) {
      case "day_off":
        return "Новый запрос на отгул";
      case "overtime":
        return "Новый запрос на переработку";
      case "report_reminder":
        return "Ежедневный отчет";
      default:
        return notificationItem.title;
    }
  };

  const getNotificationBody = (notificationItem) => {
    const requestType = notificationItem.data?.request_type;
    const username = notificationItem.data?.username;

    if (!username) {
      return notificationItem.body;
    }

    switch (requestType) {
      case "day_off":
        return `Запрос на отгул от ${username}`;
      case "overtime":
        return `Запрос на переработку от ${username}`;
      case "report_reminder":
        return notificationItem.body;
      default:
        return notificationItem.body;
    }
  };

  const items = [
    {
      key: "notifications",
      label: (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <Text strong style={{ fontSize: "14px" }}>
              Уведомления
            </Text>
            {notifications.length > 0 && (
              <button
                className="mark-all-read-button"
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAllRead}
                style={{
                  background: "none",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  padding: "2px 8px",
                  fontSize: "12px",
                  cursor: isMarkingAllRead ? "not-allowed" : "pointer",
                  color: isMarkingAllRead ? "#999" : "#1890ff",
                  transition: "all 0.2s ease",
                  opacity: isMarkingAllRead ? 0.6 : 1,
                }}
              >
                {isMarkingAllRead ? "..." : "Прочитать все"}
              </button>
            )}
          </div>
          <Spin spinning={isLoading}>
            <List
              style={{ width: 380, maxHeight: 400, overflow: "auto" }}
              dataSource={notifications}
              locale={{
                emptyText: (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Text type="secondary">Нет уведомлений</Text>
                  </div>
                ),
              }}
              renderItem={(item) => (
                <List.Item
                  className="notification-item unread"
                  onClick={() => handleNotificationClick(item)}
                  style={{
                    opacity: isMarkingRead ? 0.6 : 1,
                    pointerEvents: isMarkingRead ? "none" : "auto",
                  }}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                      }}
                    >
                      <span style={{ fontSize: "18px", marginTop: "2px" }}>
                        {getNotificationIcon(item)}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginBottom: "4px",
                          }}
                        >
                          <Text
                            className="notification-title"
                            style={{
                              color: "#000",
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getNotificationTitle(item)}
                          </Text>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: "10px",
                              backgroundColor: "#f0f0f0",
                              padding: "1px 4px",
                              borderRadius: "3px",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            {getNotificationTypeLabel(item)}
                          </Text>
                        </div>
                        <Text className="notification-text">
                          {getNotificationBody(item)}
                        </Text>
                      </div>
                    </div>
                  </Space>
                </List.Item>
              )}
            />
          </Spin>
        </div>
      ),
    },
  ];

  return (
    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
      <div style={{ position: "relative", display: "inline-block" }}>
        <BellOutlined style={{ fontSize: 24, cursor: "pointer" }} />
        <Badge count={unreadCount} />
      </div>
    </Dropdown>
  );
};

export default NotificationsDropdown;
